    class BonusCountdownBarManager {
      constructor(sweepstakeId, pollInterval, platformId, rootSelector) {
        if (typeof pollInterval === "undefined") {
          pollInterval = 3000;
        }
        this.sweepstakeId = sweepstakeId;
        this.pollInterval = pollInterval;
        this.platformId = platformId || "";
        this.rootSelector = rootSelector || "bonus-countdown-bar";
        this.bonusSpending = [];
        this.intervalId = null;
      }
  
      init() {
        const run = () => {
          this._defineBonusCountdownBar();
          this._fetchSweepstakeDetails().then((fetchedSpending) => {
            this.bonusSpending = fetchedSpending;
            this._startCartPolling();
          });
        };

        if (document.readyState === "loading") {
          document.addEventListener("DOMContentLoaded", run, { once: true });
          return;
        }

        run();
      }
  
      _defineBonusCountdownBar() {
        if (customElements.get("bonus-countdown-bar")) return;
  
        class BonusCountdownBar extends HTMLElement {
          connectedCallback() {
            this.trackElement = this.querySelector(".js-bonus-track") || this.querySelector("bonus-countdown-bar-track");
            this.animationElement = this.querySelector("bonus-countdown-bar-goal-animation");
            this.priceBubblesContainer = this.querySelector(".js-price-bubbles");
            this.milestonesContainer = this.querySelector(".js-milestones");
            this._milestoneSignature = "";
            this.customization = {};

            const minValStr = this.getAttribute("minimum-value");
            const maxValStr = this.getAttribute("maximum-value");
            const valStr = this.getAttribute("value");
            const revealDelayStr = this.getAttribute("reveal-delay");
            this.minimumValue = Number(minValStr) || 0;
            this.maximumValue = Number(maxValStr) || this.minimumValue || 0;
            this.value = Number(valStr) || 0;
            this.revealDelay = Number(revealDelayStr) || 0;
            this._readCustomizationSettings();
  
            const options = { rootMargin: "0px", threshold: 1.0 };
            this.visibilityObserver = new IntersectionObserver((entries) => {
              entries.forEach((entry) => {
                setTimeout(() => {
                  if (entry.isIntersecting) {
                    this.classList.add("is-visible");
                    if (this.value >= this.minimumValue) {
                      setTimeout(() => this._playGoalAnimation(), 1000);
                    }
                    this.visibilityObserver.disconnect();
                  }
                }, this.revealDelay);
              });
            }, options);
            this.visibilityObserver.observe(this);
  
            this._update();
          }
  
          disconnectedCallback() {
            this.classList.remove("is-visible");
            if (this.visibilityObserver) {
              this.visibilityObserver.disconnect();
            }
          }
  
          _update() {
            const minValStr = this.getAttribute("minimum-value");
            const maxValStr = this.getAttribute("maximum-value");
            const valStr = this.getAttribute("value");
            const bannerText = this.getAttribute("banner-text") || "";
            const bonusSpendingStr = this.getAttribute("bonus-spending") || "[]";
  
            this.minimumValue = Number(minValStr) || 0;
            this.maximumValue = Number(maxValStr) || this.minimumValue || 0;
            this.value = Number(valStr) || 0;
            this._readCustomizationSettings();
            this.bonusSpending = this._parseBonusSpending(bonusSpendingStr);
            this._renderMilestones(this.bonusSpending);
  
            // Insert banner text into .js-bonus-banner-text (leaves the icon intact)
            const textContainer = this.querySelector(".js-bonus-banner-text");
            if (textContainer) {
              textContainer.innerHTML = bannerText;
              textContainer.style.fontSize = `${this.customization.bannerTextSize}px`;
              textContainer.style.display = this.customization.hideBonusBanner ? "none" : "";
            }
  
            let displacement = 0;
            if (this.minimumValue > 0) {
              displacement = Math.round((this.value / this.minimumValue) * 100);
            }
            const clamped = 100 - Math.min(displacement, 100);
  
            if (this.trackElement) {
              this.trackElement.style.setProperty("--displacement", clamped + "%");
            }

            let progress = 0;
            if (this.maximumValue > 0) {
              progress = Math.min((this.value / this.maximumValue) * 100, 100);
            }

            const progressBar = this.querySelector(".percentage-bar");
            if (progressBar) {
              progressBar.style.width = progress.toFixed(4) + "%";
              progressBar.style.height = `${this.customization.barHeight}px`;
              progressBar.style.background = this.customization.barColor;
            }

            this._updateMilestoneStates();
  
            if (this.value >= this.minimumValue) {
              this._playGoalAnimation();
            }
          }

          _toBoolean(value, fallbackValue) {
            if (value === null || typeof value === "undefined" || value === "") {
              return fallbackValue;
            }
            return String(value).toLowerCase() === "true";
          }

          _readCustomizationSettings() {
            const bannerTextSize = Number(this.getAttribute("data-banner-text-size"));
            const thresholdTextSize = Number(this.getAttribute("data-threshold-text-size"));
            const milestoneLabelSize = Number(this.getAttribute("data-milestone-label-size"));
            const milestoneLabelVerticalOffset = Number(this.getAttribute("data-milestone-label-vertical-offset"));
            const milestoneHorizontalOffset = Number(this.getAttribute("data-milestone-horizontal-offset"));
            const milestoneBubbleVerticalOffset = Number(this.getAttribute("data-milestone-bubble-vertical-offset"));
            const milestoneLabelHorizontalOffset = Number(this.getAttribute("data-milestone-label-horizontal-offset"));
            const barHeight = Number(this.getAttribute("data-bar-height"));
            const glowIntensity = Number(this.getAttribute("data-glow-intensity"));
            const useCustomThresholdTextColor = this._toBoolean(
              this.getAttribute("data-use-custom-threshold-text-color"),
              false
            );
            const thresholdTextColorRaw = this.getAttribute("data-threshold-text-color") || "#374151";

            this.customization = {
              useDefaultMilestoneLanguage: this._toBoolean(
                this.getAttribute("data-use-default-milestone-language"),
                true
              ),
              customMilestoneTexts: [
                this.getAttribute("data-milestone-1-text") || "",
                this.getAttribute("data-milestone-2-text") || "",
                this.getAttribute("data-milestone-3-text") || "",
                this.getAttribute("data-milestone-4-text") || ""
              ],
              useDefaultMilestoneLabelText: this._toBoolean(
                this.getAttribute("data-use-default-milestone-label-text"),
                true
              ),
              milestoneLabelText: (this.getAttribute("data-milestone-label-text") || "Free Entries").trim(),
              bannerTextSize: Number.isFinite(bannerTextSize) && bannerTextSize > 0 ? bannerTextSize : 10,
              thresholdTextSize: Number.isFinite(thresholdTextSize) && thresholdTextSize > 0 ? thresholdTextSize : 10,
              thresholdTextColor: useCustomThresholdTextColor ? thresholdTextColorRaw : "#374151",
              milestoneLabelSize: Number.isFinite(milestoneLabelSize) && milestoneLabelSize > 0 ? milestoneLabelSize : 10,
              milestoneLabelVerticalOffset: Number.isFinite(milestoneLabelVerticalOffset) ? milestoneLabelVerticalOffset : 0,
              milestoneHorizontalOffset: Number.isFinite(milestoneHorizontalOffset) ? milestoneHorizontalOffset : 0,
              milestoneBubbleVerticalOffset: Number.isFinite(milestoneBubbleVerticalOffset) ? milestoneBubbleVerticalOffset : 0,
              milestoneLabelHorizontalOffset: Number.isFinite(milestoneLabelHorizontalOffset) ? milestoneLabelHorizontalOffset : 0,
              milestoneLabelColor: this.getAttribute("data-milestone-label-color") || "#111827",
              milestoneBubbleColor: this.getAttribute("data-milestone-bubble-color") || "#D4AF37",
              milestoneBubbleFillColor: this.getAttribute("data-milestone-bubble-fill-color") || this.getAttribute("data-milestone-bubble-color") || "#D4AF37",
              barHeight: Number.isFinite(barHeight) && barHeight > 0 ? barHeight : 8,
              barColor: this.getAttribute("data-bar-color") || "#dc2626",
              hideBonusBanner: this._toBoolean(this.getAttribute("data-hide-bonus-banner"), false),
              glowEnabled: this._toBoolean(this.getAttribute("data-glow-enabled"), false),
              glowIntensity: Number.isFinite(glowIntensity) && glowIntensity >= 0 ? glowIntensity : 12
            };
          }

          _parseBonusSpending(raw) {
            let parsed = [];
            try {
              parsed = JSON.parse(raw);
            } catch (error) {
              parsed = [];
            }

            if (!Array.isArray(parsed)) return [];

            return parsed
              .map((bonus) => ({
                spendingLimitDollar: parseFloat(bonus.spendingLimitDollar),
                bonusEntries: parseInt(bonus.bonusEntries, 10) || 0
              }))
              .filter((bonus) => Number.isFinite(bonus.spendingLimitDollar) && bonus.spendingLimitDollar > 0)
              .sort((a, b) => a.spendingLimitDollar - b.spendingLimitDollar);
          }

          _formatCurrency(value) {
            const num = Number(value);
            if (!Number.isFinite(num)) return "$0";
            const fixed = num.toFixed(2);
            return "$" + fixed.replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
          }

          _renderMilestones(bonusSpending) {
            if (!this.priceBubblesContainer || !this.milestonesContainer) return;

            const labelSuffix = this.customization.useDefaultMilestoneLabelText
              ? "Free Entries"
              : (this.customization.milestoneLabelText || "Free Entries");

            const signature = bonusSpending
              .map((bonus) => `${bonus.spendingLimitDollar}:${bonus.bonusEntries}`)
              .join("|") + `|${labelSuffix}|${this.customization.thresholdTextSize}|${this.customization.thresholdTextColor}|${this.customization.milestoneLabelSize}|${this.customization.milestoneLabelVerticalOffset}|${this.customization.milestoneHorizontalOffset}|${this.customization.milestoneBubbleVerticalOffset}|${this.customization.milestoneLabelHorizontalOffset}|${this.customization.milestoneLabelColor}|${this.customization.milestoneBubbleColor}|${this.customization.milestoneBubbleFillColor}|${this.customization.barColor}|${this.customization.barHeight}`;

            if (
              signature === this._milestoneSignature &&
              this.priceBubblesContainer.children.length === bonusSpending.length
            ) {
              return;
            }

            this._milestoneSignature = signature;
            this.priceBubblesContainer.innerHTML = "";
            this.milestonesContainer.innerHTML = "";

            if (!bonusSpending.length) return;

            const maxThresholdDollar = bonusSpending[bonusSpending.length - 1].spendingLimitDollar;

            bonusSpending.forEach((bonus, index) => {
              const thresholdCents = Math.round(bonus.spendingLimitDollar * 100);
              const positionPct = maxThresholdDollar > 0
                ? Math.min((bonus.spendingLimitDollar / maxThresholdDollar) * 100, 100)
                : 100;
              const entriesText = String(bonus.bonusEntries);

              const bubbleWrapper = document.createElement("div");
              bubbleWrapper.className = "price-bubble";
              bubbleWrapper.setAttribute("data-threshold", String(thresholdCents));
              const bubbleLeft = this.customization.milestoneHorizontalOffset === 0
                ? `${positionPct}%`
                : `calc(${positionPct}% + ${this.customization.milestoneHorizontalOffset}px)`;
              const bubbleTop = this.customization.milestoneBubbleVerticalOffset === 0
                ? "-0.6rem"
                : `calc(-0.6rem + ${this.customization.milestoneBubbleVerticalOffset}px)`;
              bubbleWrapper.style.cssText = `position:absolute; transform:translateX(-50%); top:${bubbleTop}; transition:all 300ms; left:${bubbleLeft};`;

              const milestoneBubble = document.createElement("div");
              milestoneBubble.className = "milestone-bubble";
              milestoneBubble.style.cssText = "position:relative; padding:0.375rem; border-radius:9999px; border:2px solid #d1d5db; background:#ffffff; display:flex; align-items:center; justify-content:center; overflow:hidden; transition:all 300ms; box-shadow:0 1px 2px rgba(0,0,0,0.12);";

              const bubbleFill = document.createElement("div");
              bubbleFill.className = "milestone-bubble-fill";
              bubbleFill.style.cssText = `position:absolute; inset:0; background:${this.customization.milestoneBubbleFillColor}; opacity:0; transition:all 300ms; z-index:1;`;

              const bubbleLabel = document.createElement("div");
              bubbleLabel.className = "js-bonus-threshold-text";
              bubbleLabel.style.cssText = `position:relative; font-size:${this.customization.thresholdTextSize}px; font-weight:700; color:${this.customization.thresholdTextColor}; z-index:10; white-space:nowrap;`;
              bubbleLabel.textContent = this._formatCurrency(bonus.spendingLimitDollar);

              milestoneBubble.appendChild(bubbleFill);
              milestoneBubble.appendChild(bubbleLabel);
              bubbleWrapper.appendChild(milestoneBubble);
              this.priceBubblesContainer.appendChild(bubbleWrapper);

              const milestone = document.createElement("div");
              milestone.className = "milestone";
              milestone.setAttribute("data-threshold", String(thresholdCents));
              const isLastMilestone = index === bonusSpending.length - 1;
              const baseMilestoneOffsetPx = isLastMilestone ? -16 : 0;
              const totalMilestoneOffsetPx = baseMilestoneOffsetPx + this.customization.milestoneHorizontalOffset;
              const milestoneLeft = totalMilestoneOffsetPx === 0
                ? `${positionPct}%`
                : `calc(${positionPct}% + ${totalMilestoneOffsetPx}px)`;
              milestone.style.cssText = `position:absolute; transform:translateX(-50%); transition:all 300ms; left:${milestoneLeft}; top:${this.customization.milestoneLabelVerticalOffset}px;`;

              const milestoneCol = document.createElement("div");
              milestoneCol.style.cssText = "display:flex; flex-direction:column; align-items:center; gap:0.25rem;";

              const milestoneLabelWrap = document.createElement("div");
              const milestoneLabelTranslateX = this.customization.milestoneLabelHorizontalOffset;
              milestoneLabelWrap.style.cssText = `text-align:center; line-height:1.25; white-space:nowrap; transform:translateX(${milestoneLabelTranslateX}px);`;

              const milestoneLabel = document.createElement("div");
              milestoneLabel.className = "js-bonus-milestone-label";
              milestoneLabel.style.cssText = `font-size:${this.customization.milestoneLabelSize}px; font-weight:600; color:#111827;`;
              milestoneLabel.textContent = `${entriesText} ${labelSuffix}`;

              milestoneLabelWrap.appendChild(milestoneLabel);
              milestoneCol.appendChild(milestoneLabelWrap);
              milestone.appendChild(milestoneCol);
              this.milestonesContainer.appendChild(milestone);
            });
          }

          _updateMilestoneStates() {
            const currentValue = this.value;
            const defaultMilestoneLabelColor = "#111827";

            this.querySelectorAll(".price-bubble").forEach((bubble) => {
              const threshold = Number(bubble.getAttribute("data-threshold")) || 0;
              const achieved = currentValue >= threshold;

              const shell = bubble.querySelector(".milestone-bubble");
              const fill = bubble.querySelector(".milestone-bubble-fill");
              const label = bubble.querySelector(".js-bonus-threshold-text");

              if (shell) {
                shell.style.borderColor = achieved ? this.customization.milestoneBubbleColor : "#d1d5db";
                shell.style.background = achieved ? this.customization.milestoneBubbleFillColor : "#ffffff";
                shell.classList.toggle("active", achieved);
                if (achieved && this.customization.glowEnabled) {
                  shell.style.boxShadow = `0 0 ${this.customization.glowIntensity}px ${this.customization.milestoneBubbleColor}, 0 1px 2px rgba(0,0,0,0.12)`;
                } else {
                  shell.style.boxShadow = "0 1px 2px rgba(0,0,0,0.12)";
                }
              }
              if (fill) {
                fill.style.opacity = achieved ? "1" : "0";
                fill.style.background = this.customization.milestoneBubbleFillColor;
              }
              if (label) {
                label.style.color = this.customization.thresholdTextColor;
                label.style.fontSize = `${this.customization.thresholdTextSize}px`;
              }
            });

            this.querySelectorAll(".milestone").forEach((milestone) => {
              const threshold = Number(milestone.getAttribute("data-threshold")) || 0;
              const achieved = currentValue >= threshold;
              const label = milestone.querySelector(".js-bonus-milestone-label");
              if (label) {
                label.style.color = achieved ? this.customization.milestoneLabelColor : defaultMilestoneLabelColor;
                label.style.fontSize = `${this.customization.milestoneLabelSize}px`;
              }
            });
          }
  
          _playGoalAnimation() {
            if (!this.animationElement) return;
  
            const animationShown = localStorage.getItem("bonusCountdownAnimationShown");
            if (animationShown) return;
  
            if (window.lottie && this.animationElement.getAttribute("data-url")) {
              localStorage.setItem("bonusCountdownAnimationShown", true);
              const animationUrl = this.animationElement.getAttribute("data-url");
              const lottieAnim = lottie.loadAnimation({
                container: this.animationElement,
                renderer: "svg",
                loop: false,
                autoplay: false,
                useWebWorker: false,
                path: animationUrl
              });
              lottieAnim.play();
            }
          }
        }
  
        customElements.define("bonus-countdown-bar", BonusCountdownBar);
      }
  
      _startCartPolling() {
        this._updateBonusBarWithCartSubtotal();
        if (this.intervalId) {
          clearInterval(this.intervalId);
        }
        this.intervalId = setInterval(() => {
          this._updateBonusBarWithCartSubtotal();
        }, this.pollInterval);
      }
  
      _updateBonusBarWithCartSubtotal() {
        fetch("/cart.js")
          .then((res) => res.json())
          .then((cart) => {
            if (!cart) return;
  
            const cartSubtotalCents = cart.items_subtotal_price || 0;
            const cartSubtotalFloat = cartSubtotalCents / 100.0;
  
            let totalBonusEntries = 0;
            for (let i = 0; i < this.bonusSpending.length; i++) {
              const b = this.bonusSpending[i];
              const limit = parseFloat(b.spendingLimitDollar);
              if (cartSubtotalFloat >= limit) {
                totalBonusEntries += parseInt(b.bonusEntries, 10);
              }
            }
  
            let nextBonus = null;
            for (let i = 0; i < this.bonusSpending.length; i++) {
              const limit = parseFloat(this.bonusSpending[i].spendingLimitDollar);
              if (cartSubtotalFloat < limit) {
                nextBonus = this.bonusSpending[i];
                break;
              }
            }
  
            let bannerText = "";
            if (nextBonus) {
              const entries = parseInt(nextBonus.bonusEntries, 10);
              const diff = parseFloat(nextBonus.spendingLimitDollar) - cartSubtotalFloat;
              const remaining = diff > 0 ? diff.toFixed(2) : "0.00";
              if (totalBonusEntries > 0) {
                bannerText = `Congratulations! You've earned <strong>${totalBonusEntries}</strong> Bonus Entries! You're only <strong>$${remaining}</strong> from getting <strong>${entries}</strong> additional bonus entries!`;
              } else {
                bannerText = `You're only <strong>$${remaining}</strong> from getting <strong>${entries}</strong> bonus entries!`;
              }
            } else {
              if (totalBonusEntries > 0) {
                bannerText = `Congratulations! You've earned <strong>${totalBonusEntries}</strong> Bonus Entries!`;
              } else {
                bannerText = `Add items to your cart to start earning bonus entries!`;
              }
            }
  
            const bar = document.querySelector(this.rootSelector);
            if (!bar) return;

            const customBannerText = this._resolveCustomMilestoneBannerText(bar, cartSubtotalFloat);
            if (customBannerText) {
              bannerText = customBannerText;
            } else {
              const useDefaultMilestoneLanguage = this._getBooleanSetting(
                bar.getAttribute("data-use-default-milestone-language"),
                true
              );
              const defaultMilestoneLanguageAllCaps = this._getBooleanSetting(
                bar.getAttribute("data-default-milestone-language-all-caps"),
                false
              );
              if (useDefaultMilestoneLanguage && defaultMilestoneLanguageAllCaps) {
                bannerText = bannerText.toUpperCase();
              }
            }
  
            let minValueCents = 0;
            let maxValueCents = 0;
            let nextBonusValue = "0";
            if (this.bonusSpending.length > 0) {
              const firstThreshold = parseFloat(this.bonusSpending[0].spendingLimitDollar);
              const lastThreshold = parseFloat(this.bonusSpending[this.bonusSpending.length - 1].spendingLimitDollar);
              if (Number.isFinite(firstThreshold) && firstThreshold > 0) {
                minValueCents = firstThreshold * 100;
              }
              if (Number.isFinite(lastThreshold) && lastThreshold > 0) {
                maxValueCents = lastThreshold * 100;
              }
            }

            if (nextBonus) {
              minValueCents = parseFloat(nextBonus.spendingLimitDollar) * 100;
              nextBonusValue = String(parseInt(nextBonus.bonusEntries, 10) || 0);
            }
  
            bar.setAttribute("minimum-value", minValueCents);
            bar.setAttribute("maximum-value", maxValueCents);
            bar.setAttribute("value", cartSubtotalCents);
            bar.setAttribute("banner-text", bannerText);
            bar.setAttribute("next-bonus-value", nextBonusValue);
            bar.setAttribute("bonus-spending", JSON.stringify(this.bonusSpending || []));
  
            if (typeof bar._update === "function") {
              bar._update();
            }
          });
      }

      _getBooleanSetting(value, fallbackValue) {
        if (value === null || typeof value === "undefined" || value === "") {
          return fallbackValue;
        }
        return String(value).toLowerCase() === "true";
      }

      _resolveCustomMilestoneBannerText(bar, cartSubtotalFloat) {
        const useDefaultMilestoneLanguage = this._getBooleanSetting(
          bar.getAttribute("data-use-default-milestone-language"),
          true
        );
        if (useDefaultMilestoneLanguage) return "";

        const milestoneTexts = [
          (bar.getAttribute("data-milestone-1-text") || "").trim(),
          (bar.getAttribute("data-milestone-2-text") || "").trim(),
          (bar.getAttribute("data-milestone-3-text") || "").trim(),
          (bar.getAttribute("data-milestone-4-text") || "").trim()
        ];
        const firstAvailableText = milestoneTexts.find((txt) => txt.length > 0);
        if (!firstAvailableText) return "";

        const achievedMilestoneCount = this.bonusSpending.filter((bonus) => {
          return cartSubtotalFloat >= parseFloat(bonus.spendingLimitDollar);
        }).length;

        const mappedIndex = Math.min(Math.max(achievedMilestoneCount, 0), milestoneTexts.length - 1);
        return milestoneTexts[mappedIndex] || firstAvailableText;
      }
  
      _fetchSweepstakeDetails() {
        return new Promise((resolve) => {
          const query = `
            query QueryOrganization($organizationId: Int, $platform: String, $platformId: String) {
              organization(id: $organizationId, platform: $platform, platformId: $platformId) {
                id
                plan
                sweepstakes {
                  id
                  entryManagement
                  isActive
                }
              }
            }
          `;
          const sanitizedId = this._sanitizeId(this.sweepstakeId);
          let platformId = this.platformId;
          if (!platformId) {
            const bar = document.querySelector(this.rootSelector);
            if (bar) {
              platformId = bar.getAttribute("data-platform-id") || "";
            }
          }

          if (!platformId) {
            resolve([]);
            return;
          }

          const variables = {
            organizationId: null,
            platform: "Shopify",
            platformId
          };
  
          fetch("https://backend.cleansweeps.app/graphql", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ query, variables })
          })
            .then((resp) => resp.json())
            .then((jsonData) => {
              const data = jsonData.data;
              if (!data || !data.organization || !data.organization.sweepstakes) {
                resolve([]);
                return;
              }
              const allActive = data.organization.sweepstakes.filter(s => s.isActive);
              let selected = allActive.find(s => String(s.id) === sanitizedId) || allActive[0];
              if (!selected) {
                resolve([]);
                return;
              }
              let entryManagementObj = {};
              try {
                entryManagementObj = JSON.parse(selected.entryManagement || "{}");
              } catch (err) {
                resolve([]);
                return;
              }
              let raw = entryManagementObj.bonusSpending || [];
              let result = raw
                .filter(x => x.spendingLimitEnabled === true)
                .sort((a, b) => parseFloat(a.spendingLimitDollar) - parseFloat(b.spendingLimitDollar));
              resolve(result);
            })
            .catch(() => {
              resolve([]);
            });
        });
      }
  
      _sanitizeId(str) {
        if (typeof str !== "string") str = String(str);
        return str.replace(/\D/g, "");
      }
    }

    (() => {
      const managerRegistry = new Map();

      window.initBonusCountdownBarManager = (sweepstakeId, pollInterval, rootSelector, platformId) => {
        const selector = rootSelector || "bonus-countdown-bar";
        const registryKey = selector + "::" + (sweepstakeId || "default");
        if (managerRegistry.has(registryKey)) {
          return managerRegistry.get(registryKey);
        }

        const manager = new BonusCountdownBarManager(
          sweepstakeId || "default",
          pollInterval,
          platformId,
          selector
        );
        managerRegistry.set(registryKey, manager);
        manager.init();
        return manager;
      };

      const autoInit = () => {
        const bar = document.querySelector("bonus-countdown-bar[data-sweepstake-id]");
        if (!bar) return;
        const sweepstakeId = bar.getAttribute("data-sweepstake-id") || "default";
        const selector = bar.id ? "#" + bar.id : "bonus-countdown-bar";
        const platformId = bar.getAttribute("data-platform-id") || "";
        window.initBonusCountdownBarManager(sweepstakeId, 1500, selector, platformId);
      };

      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", autoInit, { once: true });
      } else {
        autoInit();
      }
    })();
